import React from 'react';
import { Button, Tooltip, Grid } from '@mui/material';

interface ActionButtonProps {
  color: 'error' | 'info' | 'warning' | 'success' | 'primary' | 'secondary';
  label: string;
  onClick: () => void;
  disabled: boolean;
  icon: React.ReactNode;
  currentStatus: string;
  allowedStatus: string;
  tooltip?: string;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  color,
  label,
  onClick,
  disabled,
  icon,
  tooltip,
}) => {
  const getButtonVariant = () => {
    return disabled ? 'outlined' : 'contained';
  };

  const getButtonStyles = () => {
    if (disabled) {
      return {
        opacity: 0.7,
        color: 'text.secondary',
        borderColor: 'grey.300',
        backgroundColor: 'grey.50',
        '&:hover': {
          backgroundColor: 'grey.50',
        }
      };
    }

    const colorMap = {
      error: {
        background: 'linear-gradient(45deg, #f44336, #d32f2f)',
        '&:hover': {
          background: 'linear-gradient(45deg, #d32f2f, #c62828)',
        }
      },
      info: {
        background: 'linear-gradient(45deg, #2196f3, #1976d2)',
        '&:hover': {
          background: 'linear-gradient(45deg, #1976d2, #1565c0)',
        }
      },
      warning: {
        background: 'linear-gradient(45deg, #ff9800, #f57c00)',
        '&:hover': {
          background: 'linear-gradient(45deg, #f57c00, #ef6c00)',
        }
      },
      success: {
        background: 'linear-gradient(45deg, #4caf50, #2e7d32)',
        '&:hover': {
          background: 'linear-gradient(45deg, #2e7d32, #1b5e20)',
        }
      }
    };

    return colorMap[color as keyof typeof colorMap] || {};
  };

  return (
    <Grid item xs={6}>
      <Tooltip title={tooltip || ''} arrow placement="top">
        <span>
          <Button
            variant={getButtonVariant()}
            color={color}
            onClick={onClick}
            disabled={disabled}
            fullWidth
            startIcon={icon}
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              ...getButtonStyles(),
              '&.Mui-disabled': {
                borderColor: 'grey.300',
                color: 'text.secondary'
              }
            }}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    </Grid>
  );
};

export default ActionButton;